import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { GlassCard } from "@/components/GlassCard";
import { StatusBadge } from "@/components/StatusBadge";
import { BookOpen, Folder, Tag, Filter, X, Upload, FileText, CheckCircle2, RefreshCw, FolderPlus } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";

const STATUS_FILTERS = [
  { key: "", label: "All" },
  { key: "approved", label: "Approved" },
  { key: "in_review", label: "In Review" },
  { key: "draft", label: "Draft" },
  { key: "expired", label: "Expired" },
];

export default function Library() {
  const [params, setParams] = useSearchParams();
  const [sops, setSops] = useState([]);
  const [folders, setFolders] = useState([]);
  const [status, setStatus] = useState(params.get("status") || "");
  const [folderId, setFolderId] = useState(params.get("folder_id") || "");
  const [q, setQ] = useState(params.get("q") || "");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { user } = useAuth();

  // Separate Bulk Import states
  const [targetDept, setTargetDept] = useState("Operations");
  const [projectName, setProjectName] = useState("Operations Launch");
  const [importing, setImporting] = useState(false);
  const [importSuccess, setImportSuccess] = useState(false);

  const canCreate = user?.role === "champion" || user?.role === "admin";

  const loadFoldersAndList = async () => {
    try {
      const { data } = await api.get("/folders");
      setFolders(data);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    loadFoldersAndList();
  }, []);

  const loadSopsList = async () => {
    setLoading(true);
    try {
      const q2 = params.get("q") || "";
      const s2 = params.get("status") || "";
      const f2 = params.get("folder_id") || "";
      const qs = new URLSearchParams();
      if (q2) qs.set("q", q2);
      if (s2) qs.set("status", s2);
      if (f2) qs.set("folder_id", f2);
      const { data } = await api.get(`/sops?${qs.toString()}`);
      setSops(data);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSopsList();
  }, [params]);

  const setParam = (k, v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v);
    else next.delete(k);
    setParams(next);
  };

  const folderMap = useMemo(() => Object.fromEntries(folders.map((f) => [f.id, f])), [folders]);

  // Bulk Import SOPs with active Project/Folder Creation and Department selections
  const handleBulkImportJson = async (e) => {
    e.preventDefault();
    setImporting(true);
    setImportSuccess(false);
    
    try {
      // 1. Automatically create a new Project Folder if requested
      let activeFolderId = "";
      const existing = folders.find(f => f.name.toLowerCase() === projectName.toLowerCase());
      
      if (existing) {
        activeFolderId = existing.id;
      } else {
        const folderRes = await api.post("/folders", {
          name: projectName,
          description: `Custom bulk project for ${targetDept}`
        });
        activeFolderId = folderRes.data.id;
      }
      
      // 2. Draft the bulk SOP payload mapped directly to selected params
      const payload = {
        sops: [
          {
            title: `${projectName}: Sanitization Standard`,
            description: `Automated Class A cleanroom protocol mapped for ${projectName}.`,
            content: "<h2>1. Standard Airlocks</h2><p>Laminar airflow filters wiped with Isopropyl Alcohol solution.</p>",
            department: targetDept,
            tags: ["semiconductor", "bulk-imported", projectName.toLowerCase().replace(/\s+/g, "-")]
          },
          {
            title: `${projectName}: Expense Travel Audit`,
            description: `Financial traveler expenses verification checklist for ${projectName}.`,
            content: "<h2>1. Verification Guidelines</h2><p>Audit traveler receipts and ledger balances daily.</p>",
            department: targetDept,
            tags: ["accounting", "stripe", "bulk-imported", projectName.toLowerCase().replace(/\s+/g, "-")]
          }
        ]
      };
      
      // 3. Post bulk import
      await api.post("/sops/bulk-import", payload);
      
      // 4. Update the folder id reference for newly created sops (backend fallback)
      const { data: updatedSops } = await api.get("/sops");
      for (const s of updatedSops) {
        if (s.title.startsWith(projectName) && !s.folder_id) {
          await api.patch(`/sops/${s.id}`, { folder_id: activeFolderId });
        }
      }
      
      setImportSuccess(true);
      loadFoldersAndList();
      loadSopsList();
      setTimeout(() => setImportSuccess(false), 4000);
    } catch (err) {
      console.error(err);
      alert("Failed to process bulk project import.");
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="library-page">
      <header className="flex flex-col md:flex-row md:items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold text-white tracking-tight">SOP Library</h1>
          <p className="text-[#9AA4B2] text-sm mt-1">Search, filter, bulk import procedures, or upload files to auto-draft SOPs instantly.</p>
        </div>
        {canCreate && (
          <div className="flex gap-2">
            <button
              onClick={() => navigate("/create")}
              className="btn-primary-glow rounded-full px-5 py-3 text-sm font-medium whitespace-nowrap"
              data-testid="library-create-btn"
            >
              + New SOP
            </button>
          </div>
        )}
      </header>

      {/* SEPARATE BULK IMPORT SECTION (Project & Department selection) */}
      {canCreate && (
        <GlassCard className="p-5 border border-indigo-500/20 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-48 h-48 bg-indigo-500/5 rounded-full blur-3xl animate-pulse" />
          
          <div className="relative">
            <div className="flex items-center gap-2 mb-3 border-b border-white/5 pb-2">
              <FolderPlus size={18} className="text-indigo-400" />
              <h3 className="font-display text-lg text-white">Bulk Project Importer Hub</h3>
            </div>
            
            <form onSubmit={handleBulkImportJson} className="grid sm:grid-cols-3 gap-4 text-xs items-end">
              <div>
                <label className="block text-[10px] uppercase tracking-widest text-[#9AA4B2] mb-1.5 font-bold">1. Select Target Department</label>
                <select
                  value={targetDept}
                  onChange={(e) => setTargetDept(e.target.value)}
                  className="w-full bg-[#05070D]/80 border border-white/10 rounded-xl p-2.5 text-xs text-white focus:outline-none focus:border-indigo-400 transition"
                >
                  <option value="Operations">Operations</option>
                  <option value="IT & Security">IT & Security</option>
                  <option value="Finance">Finance</option>
                  <option value="Human Resources">HR & Personnel</option>
                </select>
              </div>

              <div>
                <label className="block text-[10px] uppercase tracking-widest text-[#9AA4B2] mb-1.5 font-bold">2. Assign / Create Project Folder</label>
                <input
                  type="text"
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  className="w-full bg-[#05070D]/80 border border-white/10 rounded-xl p-2.5 text-xs text-white focus:outline-none focus:border-indigo-400 transition"
                  placeholder="e.g. Operations Launch"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={importing}
                className="btn-primary-glow rounded-xl py-2.5 font-bold text-xs flex items-center justify-center gap-1.5"
              >
                {importing ? <RefreshCw className="animate-spin" size={13} /> : <Upload size={13} />}
                Execute Structured Bulk Import
              </button>
            </form>
          </div>

          {importSuccess && (
            <div className="mt-3 p-2.5 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-xs text-emerald-300 flex items-center gap-2">
              <CheckCircle2 size={14} /> Project folder <strong>'{projectName}'</strong> created and bulk SOP drafts imported successfully!
            </div>
          )}
        </GlassCard>
      )}

      {/* Filters */}
      <GlassCard className="p-4">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1.5 text-[#9AA4B2] text-xs mr-2">
            <Filter size={14} /> Status:
          </div>
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.key || "all"}
              onClick={() => {
                setStatus(f.key);
                setParam("status", f.key);
              }}
              data-testid={`filter-status-${f.key || "all"}`}
              className={`px-3 py-1.5 rounded-full text-xs border transition ${
                (status || "") === f.key
                  ? "bg-white/10 border-white/25 text-white"
                  : "bg-white/[0.02] border-white/10 text-[#9AA4B2] hover:text-white"
              }`}
            >
              {f.label}
            </button>
          ))}
          <div className="w-px h-6 bg-white/10 mx-2" />
          <div className="flex items-center gap-1.5 text-[#9AA4B2] text-xs">
            <Folder size={14} /> Folder:
          </div>
          <select
            value={folderId}
            onChange={(e) => {
              setFolderId(e.target.value);
              setParam("folder_id", e.target.value);
            }}
            data-testid="filter-folder"
            className="px-3 py-1.5 rounded-full text-xs bg-white/[0.04] border border-white/10 text-white focus:outline-none focus:border-cyan-400/60"
          >
            <option value="">All folders</option>
            {folders.map((f) => (
              <option key={f.id} value={f.id}>{f.name}</option>
            ))}
          </select>
          {q && (
            <div className="ml-auto flex items-center gap-2 text-xs text-cyan-300">
              Searching “{q}”
              <button
                onClick={() => {
                  setQ("");
                  setParam("q", "");
                }}
                className="p-1 rounded-full hover:bg-white/10"
                aria-label="Clear search"
              >
                <X size={12} />
              </button>
            </div>
          )}
        </div>
      </GlassCard>

      {/* Grid */}
      {loading ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <GlassCard key={i} className="p-6 h-40 animate-pulse-glow" />
          ))}
        </div>
      ) : sops.length === 0 ? (
        <GlassCard className="p-16 text-center" data-testid="library-empty">
          <div className="mx-auto w-14 h-14 rounded-2xl glass flex items-center justify-center text-indigo-300 mb-4">
            <BookOpen size={22} />
          </div>
          <div className="text-white font-display text-lg">No SOPs match those filters.</div>
          <div className="text-[#9AA4B2] text-sm mt-1">Try clearing filters, or create the first one.</div>
        </GlassCard>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sops.map((s) => (
            <GlassCard
              key={s.id}
              hoverable
              className="p-6 cursor-pointer"
              onClick={() => navigate(`/sops/${s.id}`)}
              data-testid={`sop-card-${s.id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-indigo-500/30 to-cyan-500/20 border border-white/10 flex items-center justify-center text-indigo-200">
                  <BookOpen size={18} />
                </div>
                <StatusBadge status={s.status} />
              </div>
              <h3 className="mt-4 font-display text-lg text-white leading-snug line-clamp-2">{s.title}</h3>
              <p className="mt-1 text-sm text-[#9AA4B2] line-clamp-2">{s.description}</p>
              <div className="mt-4 flex items-center gap-2 text-[11px] text-[#5B6472]">
                <span className="px-2 py-0.5 rounded-full bg-white/[0.04] border border-white/10">v{s.current_version_number}</span>
                {s.folder_id && folderMap[s.folder_id] && (
                  <span className="px-2 py-0.5 rounded-full bg-white/[0.04] border border-white/10">
                    {folderMap[s.folder_id].name}
                  </span>
                )}
                {s.department && (
                  <span className="px-2 py-0.5 rounded-full bg-white/[0.04] border border-white/10">
                    {s.department}
                  </span>
                )}
              </div>
              {s.tags?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {s.tags.slice(0, 4).map((t) => (
                    <span key={t} className="inline-flex items-center gap-1 text-[10px] text-[#9AA4B2] px-2 py-0.5 rounded-full border border-white/5">
                      <Tag size={10} /> {t}
                    </span>
                  ))}
                </div>
              )}
            </GlassCard>
          ))}
        </div>
      )}
    </div>
  );
}
